import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { Observable, BehaviorSubject } from 'rxjs';
import { CacheEvent } from './cache-event.model';
import { StoreUserService, User } from '@app/core/state/store-user.service';
import { SidenavService } from '@app/core/layout/sidenav.service';
import { ListUserDataService } from '../user/list-user-data.service';
import { GetUseritselfService } from '../user/get-useritself.service';
import { StoreAuthService } from '@app/core/auth/store-auth.service';
import { ListUsersService } from '../user/list-users.service';
import { id } from 'date-fns/locale';
import { ActivatedRoute, Router } from '@angular/router';
import { ListMediasService } from '../media/list-medias.service';
import { ListRecordsService } from '../record/list-records.service';
import { ListRecordsDownloadRequestsService } from '../record/list-records-download-requests.service';
import { LOCALE_ID, Inject } from '@angular/core';
import { GetRecordService } from '../record/get-record.service';
import { RecordData, QuestionnaireAnswer, TypesUtils } from '../../types';
import {
  STAFF_JOB_TITLES,
  STAFF_ROLES,
  QUOTA_MODELS,
  SECTION_GROUP_ADMIN_ROLES,
  SECTION_GROUP_ADMIN_ROLES_MUST_HAVE,
  SYSTEM_ADMIN_ROLES,
  SECTION_GROUP_FUNCTIONS,
  USER_JOB_TITLES,
  STATUS
} from '../../constants/app-constants';
import { DATABASE_CACHE_SERVICE_I18N } from './database-cache.service.i18n';

@Injectable({
  providedIn: 'root'
})

export class DatabaseCacheService {

  public static readonly TAG = "commonVariables";

  parametersUpdated = new Subject<CacheEvent>();

  usersDataCache = new Map<string, unknown>();

  private cachedLoginUserResponse: any = null;
  private pendingLoginUserPromise: Promise<any> | null = null;
  private pendingLoginUserAndSectionPromise: Promise<any> | null = null;
  private pendingLoginUserAndSectionKey: string | null = null;

  // In-flight de-dup for per-user list fetches. Without this, parallel
  // callers (e.g. record list + media list mounting together, or a guard
  // re-running on rapid navigation) each fire a fresh HTTP request even
  // though the first one hasn't resolved yet. Keyed by `${sectionId}|${userId}`.
  private pendingMediasFetch = new Map<string, Promise<void>>();
  private pendingRecordsFetch = new Map<string, Promise<void>>();
  private pendingGostsFetch = new Map<string, Promise<void>>();
  private pendingUserDataHistoryFetch = new Map<string, Promise<void>>();
  private pendingUserDataFetch = new Map<string, Promise<void>>();
  private pendingRecordsDownloadRequestsFetch = new Map<string, Promise<void>>();

  // In-flight de-dup for getGostRecord. gost-matrix / gost-summary / gost-digest
  // can each request the same recordId concurrently on mount, producing
  // duplicate /recordWithData/<id> GETs. Keyed by `${sectionId}|${recordId}`.
  private pendingGostRecordFetch = new Map<string, Promise<any>>();

  // Constants imported from app-constants.ts
  public readonly staffJobTitles = STAFF_JOB_TITLES;
  public readonly staffRoles = STAFF_ROLES;
  public readonly quotaModels = QUOTA_MODELS;
  public readonly sectionGroupAdminRoles = SECTION_GROUP_ADMIN_ROLES;
  public readonly sectionGroupAdminRolesMustHave = SECTION_GROUP_ADMIN_ROLES_MUST_HAVE;
  public readonly systemAdminRoles = SYSTEM_ADMIN_ROLES;
  public readonly SectionGroupFunctions = SECTION_GROUP_FUNCTIONS;
  public readonly userJobTitles = USER_JOB_TITLES;
  public readonly Status = STATUS;

  constructor(
    private storeUser: StoreUserService,
    public sidenavService: SidenavService,
    private listUserDataService : ListUserDataService,
    private getUseritselfService: GetUseritselfService,
    private storeAuth: StoreAuthService,
    private listUsersService: ListUsersService,
    private listMediasService : ListMediasService,
    private listRecordsService: ListRecordsService,
    private listRecordsDownloadRequestsService : ListRecordsDownloadRequestsService,
    private router : Router,
    private getRecordService : GetRecordService,
    @Inject(LOCALE_ID) public locale: string,

  ) {

    
  }

  // set/get usersList ---------------------
  // usersListとして実際は userWithUserData の配列を保持する
  private setUsersList(newUsersList) {
    if (newUsersList) {
      for (var user of newUsersList) {
        user.user.userName = user.user.code ?? user.user.lastName + ' ' + user.user.firstName;
      }
    }

    this.setSessionData('usersList', newUsersList);
  }

  public forceOverwriteUsersList(newUsersList) {
    // 通常は localStorage にキャッシュしてある usersList を直接上書きはしない。どうしてもキャッシュされているものを直接編集したいときだけこの関数を呼び出す
    for (var user of newUsersList) {
      user.user.userName = user.user.code ?? user.user.lastName + ' ' + user.user.firstName;
    }

    this.setSessionData('usersList', newUsersList);
  }

  getUsersList() {
    return this.getSessionData('usersList');
  }

  // set/get selectedUserId ---------------------
  async setSelectedUserId(userId : string){
    while (this.getSelectedSection() === null) {
      // 選択されている Section が設定されるまで非同期で待つ
      await this.delay(100); // 100ミリ秒待機
    }

    // これまでに設定されていたユーザと変更が無い場合はなにもせず終了
    // ただし、各コンポーネント内のデータリフレッシュをトリガーするため Subjectを通じての通知は行う
    const prevSelectedUserId = this.getSelectedUserId();

    if (prevSelectedUserId == userId){
      this.parametersUpdated.next({ type: 'selectedUserChanged', userId, previousUserId: prevSelectedUserId });
      return;
    }

    // 存在しないユーザが指定されたときは 404
    // getUsersList() can be null during section switching - treat as not found
    const usersList = this.getUsersList();
    const selectedUser = usersList?.find(u => u.user.userId == userId);
    if (selectedUser == null){
      this.router.navigate(['/not-found']);
      return;
    }

    // 「現在のユーザ」を設定
    this.setSessionData('selectedUserId', userId);

    this.storeUser.setSelectedUser({ userId: selectedUser.user.userId, userName: selectedUser.user.code ?? selectedUser.user.lastName + ' ' + selectedUser.user.firstName } as User);

    // キャッシュされているデータをクリア
    this.setUserDataHistoryDate(userId, null);

    // 更新のお知らせ
    this.parametersUpdated.next({ type: 'selectedUserChanged', userId, previousUserId: prevSelectedUserId });
    return;
  }

  getSelectedUserId(){
    return this.getSessionData('selectedUserId');
  }

  prepareUserDataHistory(userId: string, forceRefresh : boolean) {

    if (forceRefresh){
      this.setUserDataHistoryDate(userId, null);
    }

    if (this.getUserDataHistoryDate().length !== 0) {
      this.parametersUpdated.next({ type: 'userDataHistoryUpdated', userId, sectionId: this.getSelectedSection()?.sectionId });
      return;
    }

    const sectionId = this.getSelectedSection()?.sectionId;
    if (!sectionId) {
      this.parametersUpdated.next({ type: 'userDataHistoryUpdated', userId, sectionId: null });
      return;
    }

    const key = `${sectionId}|${userId}`;
    if (this.pendingUserDataHistoryFetch.has(key)) {
      return;
    }

    const fetch = new Promise<void>((resolve) => {
      this.listUserDataService.listUserData(userId, sectionId, true).subscribe(
        response => {

          const userDataHistoryDate = (response as any).sort((a, b) => new Date(b.asOfDate).getTime() - new Date(a.asOfDate).getTime()); // 更新日の降順に並べ替える

          this.setUserDataHistoryDate(userId, userDataHistoryDate);

          this.parametersUpdated.next({ type: 'userDataHistoryUpdated', userId, sectionId });
          resolve();
        },
        error => {
          this.parametersUpdated.next({ type: 'userDataHistoryUpdated', userId, sectionId });
          resolve();
        }
      );
    }).finally(() => {
      this.pendingUserDataHistoryFetch.delete(key);
    });

    this.pendingUserDataHistoryFetch.set(key, fetch);
  }

  prepareUserMediasList(userId: string, forceRefresh : boolean) {

    if (forceRefresh){
      this.setUserMediasList(userId, null);
    }

    if (this.getUserMediasList() != null) {
      this.parametersUpdated.next({ type: 'mediasUpdated', userId, sectionId: this.getSelectedSection()?.sectionId });
      return;
    }

    const sectionId = this.getSelectedSection()?.sectionId;
    if (!sectionId) {
      this.parametersUpdated.next({ type: 'mediasUpdated', userId, sectionId: null });
      return;
    }

    const key = `${sectionId}|${userId}`;
    if (this.pendingMediasFetch.has(key)) {
      return;
    }

    const fetch = new Promise<void>((resolve) => {
      this.listMediasService.listMedias(sectionId, userId).subscribe(
        response => {

          // manifest URL のブラウザ別書き換えはキャッシュ時には行わない。
          // 再生直前に buildMkSourceConfig（shared/utils/streaming-url.utils）で
          // 一元適用するため、生 URL のままキャッシュする。
          const mediasList = (response as any);

          this.setUserMediasList(userId, mediasList);

          this.parametersUpdated.next({ type: 'mediasUpdated', userId, sectionId });
          resolve();
        },
        error => {
          this.setUserMediasList(userId, []);
          this.parametersUpdated.next({ type: 'mediasUpdated', userId, sectionId });
          resolve();
        }
      );
    }).finally(() => {
      this.pendingMediasFetch.delete(key);
    });

    this.pendingMediasFetch.set(key, fetch);
  }

  prepareUserGostList(userId: string, forceRefresh : boolean, latestOnly: boolean = false){
    if (!latestOnly) {
      this.prepareUserRecordsList(userId, forceRefresh);
      return;
    }

    if (forceRefresh){
      this.setUserGostsList(userId, null);
    }

    if (this.getUserGostsList() != null) {
      this.parametersUpdated.next({ type: 'gostsUpdated', userId, sectionId: this.getSelectedSection()?.sectionId });
      return;
    }

    const sectionId = this.getSelectedSection()?.sectionId;
    if (!sectionId) {
      this.parametersUpdated.next({ type: 'gostsUpdated', userId, sectionId: null });
      return;
    }

    const key = `${sectionId}|${userId}`;
    if (this.pendingGostsFetch.has(key)) {
      return;
    }

    const fetch = new Promise<void>((resolve) => {
      this.listRecordsService.listRecords(sectionId, userId, 1, 'GOST', true).subscribe({
        next: (response) => {
          const gostsList = (response as any) ?? [];
          this.setUserGostsList(userId, gostsList);
          this.parametersUpdated.next({ type: 'gostsUpdated', userId, sectionId });
          resolve();
        },
        error: (error) => {
          this.parametersUpdated.next({ type: 'gostsUpdated', userId, sectionId });
          resolve();
        }
      });
    }).finally(() => {
      this.pendingGostsFetch.delete(key);
    });

    this.pendingGostsFetch.set(key, fetch);
  }

  prepareUserRecordsList(userId: string, forceRefresh : boolean) {

    if (forceRefresh){
      this.setUserRecordsList(userId, null);
    }

    if (this.getUserRecordsList() != null) {
      this.parametersUpdated.next({ type: 'recordsUpdated', userId, sectionId: this.getSelectedSection()?.sectionId });
      return;
    }

    const sectionId = this.getSelectedSection()?.sectionId;
    if (!sectionId) {
      this.parametersUpdated.next({ type: 'recordsUpdated', userId, sectionId: null });
      return;
    }

    const key = `${sectionId}|${userId}`;
    if (this.pendingRecordsFetch.has(key)) {
      return;
    }

    const fetch = new Promise<void>((resolve) => {
      this.listRecordsService.listRecords(sectionId, userId, null, null, true).subscribe({
        next: (response) => {
          const recordsList = (response as any);

          this.setUserRecordsList(userId, recordsList);

          this.parametersUpdated.next({ type: 'recordsUpdated', userId, sectionId });
          resolve();
        },
        error: (error) => {
          this.parametersUpdated.next({ type: 'recordsUpdated', userId, sectionId });
          resolve();
        }
      });
    }).finally(() => {
      this.pendingRecordsFetch.delete(key);
    });

    this.pendingRecordsFetch.set(key, fetch);
  }

  prepareRecordsDownloadRequestsList(forceRefresh : boolean) {

    if (forceRefresh){
      this.setRecordsDownloadRequestsList(null);
    }

    if (this.getRecordsDownloadRequestsList() != null) {
      this.parametersUpdated.next({ type: 'recordsDownloadRequestsUpdated', sectionId: this.getSelectedSection()?.sectionId });
      return;
    }

    const sectionId = this.getSelectedSection()?.sectionId;
    if (!sectionId) {
      this.parametersUpdated.next({ type: 'recordsDownloadRequestsUpdated', sectionId: null });
      return;
    }

    if (this.pendingRecordsDownloadRequestsFetch.has(sectionId)) {
      return;
    }

    const fetch = new Promise<void>((resolve) => {
      this.listRecordsDownloadRequestsService.listRecordsDownloadRequests(sectionId).subscribe({
        next: (response) => {
          const requestsList = (response as any);

          this.setRecordsDownloadRequestsList(requestsList);

          this.parametersUpdated.next({ type: 'recordsDownloadRequestsUpdated', sectionId });
          resolve();
        },
        error: (error) => {
          this.parametersUpdated.next({ type: 'recordsDownloadRequestsUpdated', sectionId });
          resolve();
        }
      });
    }).finally(() => {
      this.pendingRecordsDownloadRequestsFetch.delete(sectionId);
    });

    this.pendingRecordsDownloadRequestsFetch.set(sectionId, fetch);
  }

  prepareUserDataList(userId: string, forceRefresh : boolean) {

    if (forceRefresh){
      this.setUserDataList(userId, null);
    }

    if (this.getUserDataList() != null) {
      this.parametersUpdated.next({ type: 'userDataUpdated', userId, sectionId: this.getSelectedSection()?.sectionId });
      return;
    }

    const sectionId = this.getSelectedSection()?.sectionId;
    if (!sectionId) {
      this.parametersUpdated.next({ type: 'userDataUpdated', userId, sectionId: null });
      return;
    }

    const key = `${sectionId}|${userId}`;
    if (this.pendingUserDataFetch.has(key)) {
      return;
    }

    const fetch = new Promise<void>((resolve) => {
      this.listUserDataService.listUserData(userId, sectionId).subscribe(
        response => {
          const userDataList = (response as any).sort((a, b) => new Date(b.asOfDate).getTime() - new Date(a.asOfDate).getTime()); // asofDateの降順に並べ替える

          this.setUserDataList(userId, userDataList);

          this.parametersUpdated.next({ type: 'userDataUpdated', userId, sectionId });
          resolve();
        },
        error => {
          this.parametersUpdated.next({ type: 'userDataUpdated', userId, sectionId });
          resolve();
        }
      );
    }).finally(() => {
      this.pendingUserDataFetch.delete(key);
    });

    this.pendingUserDataFetch.set(key, fetch);
  }

  // set/get userDataHistoryDate ---------------------
  private setUserDataHistoryDate(userId, userDataHistoryDate){
    this.setOnlineCacheDataWithUserId(userId, 'userDataHistoryDate', userDataHistoryDate);
  }

  getUserDataHistoryDate() {
    let userDataHistoryDate = this.getOnlineCacheDataForSelectedUser('userDataHistoryDate');

    if (userDataHistoryDate == null){
      userDataHistoryDate = [];
    }

    return userDataHistoryDate;
  }

  // set/get mediasList ---------------------
  private setUserMediasList(userId, mediasList){
    this.setOnlineCacheDataWithUserId(userId, 'mediasList', mediasList);
  }

  getUserMediasList() {
    let mediasList = this.getOnlineCacheDataForSelectedUser('mediasList');

    return mediasList;
  }

  /**
   * 現在選択中ユーザーのメディアキャッシュから指定IDを即時に除外する。
   * 削除API成功後の画面間不整合を防ぐために使用する。
   */
  evictUserMediaFromCache(mediaId: string): void {
    if (!mediaId) {
      return;
    }

    const userId = this.getSelectedUserId();
    if (!userId) {
      return;
    }

    const mediasList = this.getUserMediasList();
    if (!Array.isArray(mediasList)) {
      return;
    }

    const next = mediasList.filter((media: any) => media?.id !== mediaId);
    if (next.length === mediasList.length) {
      return;
    }

    this.setUserMediasList(userId, next);
    this.parametersUpdated.next({ type: 'mediasUpdated', userId, sectionId: this.getSelectedSection()?.sectionId });
  }

  // set/get recordsDownloadRequestsList ---------------------
  private setRecordsDownloadRequestsList(requestsList){
    this.setOnlineCacheData('recordsDownloadRequestsList', requestsList);
  }

  getRecordsDownloadRequestsList() {
    let requestsList = this.getOnlineCacheData('recordsDownloadRequestsList');

    return requestsList;
  }

  // set/get recordsList ---------------------
  private setUserRecordsList(userId, recordsList){
    this.setOnlineCacheDataWithUserId(userId, 'recordsList', recordsList);
  }

  getUserRecordsList() {
    let recordsList = this.getOnlineCacheDataForSelectedUser('recordsList');

    return recordsList;
  }

  private setUserGostsList(userId, gostsList){
    this.setOnlineCacheDataWithUserId(userId, 'gostsList', gostsList);
  }

  appendUserRecordsList(newRecord){

    const recordsList = this.getUserRecordsList();
    if (recordsList == null){
      return;
    }

    const duplicate = recordsList.find(r => r.id == newRecord.id);
    if(duplicate){
      return;
    }

    recordsList.push(newRecord);

    this.setUserRecordsList(newRecord.meta.user.userId, recordsList);

  }

  getUserGostsList() {
    let recordsList = this.getOnlineCacheDataForSelectedUser('recordsList');

    if (recordsList == null){
      recordsList = this.getOnlineCacheDataForSelectedUser('gostsList');
    }

    if (recordsList == null){
      return null;
    }

    const acceptedRecordTypes = new Set(['GOST', 'GOST_DATA', 'GOST_DATA_2']);
    let gostsList = recordsList.filter((item: any) => {
      const recordType = (item?.data?.recordType ?? '').toString().toUpperCase();
      return acceptedRecordTypes.has(recordType);
    });

    const toTimestamp = (value: unknown): number => {
      if (typeof value !== "string" || value.trim() === "") {
        return Number.NaN;
      }

      const direct = Date.parse(value);
      if (!Number.isNaN(direct)) {
        return direct;
      }

      // "YYYY/MM/DD" フォーマットの互換性向上
      const normalized = value.replace(/\//g, "-");
      const normalizedTs = Date.parse(normalized);
      return Number.isNaN(normalizedTs) ? Number.NaN : normalizedTs;
    };

    gostsList.sort((a, b) => {
      const asOfA = toTimestamp(a?.data?.asOfDate);
      const asOfB = toTimestamp(b?.data?.asOfDate);
      const asOfAValid = Number.isFinite(asOfA);
      const asOfBValid = Number.isFinite(asOfB);

      // まず評価日（asOfDate）で降順
      if (asOfAValid && asOfBValid && asOfA !== asOfB) {
        return asOfB - asOfA;
      }
      if (asOfAValid && !asOfBValid) {
        return -1;
      }
      if (!asOfAValid && asOfBValid) {
        return 1;
      }

      // 同日/評価日欠損時は作成日時で降順
      const createdA = toTimestamp(a?.data?.createdAt);
      const createdB = toTimestamp(b?.data?.createdAt);
      const fallbackA = Number.isFinite(createdA) ? createdA : 0;
      const fallbackB = Number.isFinite(createdB) ? createdB : 0;
      return fallbackB - fallbackA;
    });

    return gostsList;
  }

  getGostRecord(recordId): Promise<any> {
    if (recordId == undefined || recordId === '') {
      return Promise.reject('Invalid recordId');
    }

    const selectedUserId = this.getSelectedUserId();
    const recordCacheKey = `gostRecord:${recordId}`;
    const cachedRecord = this.getOnlineCacheDataForSelectedUser(recordCacheKey);
    if (cachedRecord != null) {
      return Promise.resolve(cachedRecord);
    }

    const sectionId = this.getSelectedSection()?.sectionId;
    if (!sectionId) {
      return Promise.reject('No section selected');
    }

    const inflightKey = `${sectionId}|${recordId}`;
    const existing = this.pendingGostRecordFetch.get(inflightKey);
    if (existing) {
      return existing;
    }

    const fetch = new Promise<any>((resolve, reject) => {
      this.getRecordService.getRecord(recordId, sectionId).subscribe(
        res => {
          const responseData = res as any;
          const result = TypesUtils.convertJsonToRecords([responseData], false);
          const gostRecord = result.records[0];

          if (selectedUserId != null && selectedUserId !== '') {
            this.setOnlineCacheDataWithUserId(selectedUserId, recordCacheKey, gostRecord);
          }

          resolve(gostRecord);
        },
        error => {
          reject(error);
        }
      );
    }).finally(() => {
      this.pendingGostRecordFetch.delete(inflightKey);
    });

    this.pendingGostRecordFetch.set(inflightKey, fetch);
    return fetch;
  }

  // set/get userDataList ---------------------
  private setUserDataList(userId, userDataList){
    this.setOnlineCacheDataWithUserId(userId, 'userDataList', userDataList);
  }

  getUserDataList() {
    let userDataList = this.getOnlineCacheDataForSelectedUser('userDataList');

    return userDataList;
  }

  getSelectedUserWithUserData(id?: string) {
    const usersList = this.getUsersList();

    if (id) {
      //if (this.usersList != null) {
      if (usersList != null) {
        //return this.usersList.filter(u => u.user.userId === id)[0];
        return usersList.filter(u => u.user.userId === id)[0];
      }

    }
    else {
      //if (this.usersList != null) {
      if (usersList != null) {
        return usersList.filter(u => u.user.userId === this.getSelectedUserId())[0];
      }
      else {
        let selectedUserWithUserData = JSON.parse(sessionStorage.getItem('selectedUserWithUserData'));
        if (selectedUserWithUserData != null) {
          return selectedUserWithUserData;
        }
      }
    }
  }

  async setLoginUserAndSelectedSection(sectionId? : string) : Promise<any> {
    if (this.canReuseLoginUserData(sectionId)) {
      await this.ensureSectionSelectionForResponse(this.cachedLoginUserResponse, sectionId);
      return this.cachedLoginUserResponse;
    }

    const normalizedKey = sectionId ?? '';
    if (this.pendingLoginUserAndSectionPromise && this.pendingLoginUserAndSectionKey === normalizedKey) {
      return this.pendingLoginUserAndSectionPromise;
    }

    const request = this.createLoginUserAndSelectedSectionRequest(sectionId);
    this.pendingLoginUserAndSectionPromise = request;
    this.pendingLoginUserAndSectionKey = normalizedKey;

    request.finally(() => {
      this.pendingLoginUserAndSectionPromise = null;
      this.pendingLoginUserAndSectionKey = null;
    });

    return request;
  }

  /**
   * Refresh all data without page reload.
   * Clears cache and re-fetches login user and section data.
   * @param sectionId Optional section ID to select after refresh
   */
  async refreshAllData(sectionId?: string): Promise<any> {
    // Clear cached data
    this.cachedLoginUserResponse = null;
    this.usersDataCache.clear();

    // Re-fetch data
    const responseData = await this.setLoginUserAndSelectedSection(sectionId);
    this.parametersUpdated.next({ type: 'allDataUpdated' });
    return responseData;
  }

  /**
   * Refresh data for a specific section.
   * @param sectionId The section ID to refresh
   */
  async refreshSection(sectionId: string): Promise<void> {
    // Clear user-specific cache for this section
    this.usersDataCache.clear();

    // Re-select the section to refresh its data
    await this.setSelectedSectionById(sectionId);
    this.parametersUpdated.next({ type: 'sectionRefreshed', sectionId });
  }

  /**
   * Re-fetch the users list for the current section from the API.
   * Keeps existing data visible during fetch (no cache clear).
   */
  refetchUsersList(): void {
    const selectedSection = this.getSelectedSection();
    if (!selectedSection?.sectionId) {
      return;
    }

    const sectionId = selectedSection.sectionId;

    // Re-fetch from API (keep existing cache intact for ghost skeleton)
    this.listUsersService.listUserWithUserData(sectionId, false, true, true).subscribe(
      res => {
        if (this.getSelectedSection()?.sectionId === sectionId) {
          this.setUsersList(res);
          this.parametersUpdated.next({ type: 'usersListUpdated', sectionId });
        }
      },
      error => {
        console.error('[DatabaseCacheService] Failed to refresh users list:', error);
        if (this.getSelectedSection()?.sectionId === sectionId) {
          this.parametersUpdated.next({ type: 'usersListUpdated', sectionId });
        }
      }
    );
  }

  setLoginUser() : Promise<any> {
    if (this.pendingLoginUserPromise) {
      return this.pendingLoginUserPromise;
    }

    this.pendingLoginUserPromise = new Promise((resolve, reject) => {
      this.getUseritselfService.getUseritself().subscribe(
        res => {
          let responseData = res as any;
          if (!responseData.error) {
            this.setStorageCache(responseData);
          }
          else{
            this.resetSelectedSection();
          }

          resolve(responseData);
        },
        error => {
          this.resetSelectedSection();
          reject(error);
        });
      });
    this.pendingLoginUserPromise.finally(() => {
      this.pendingLoginUserPromise = null;
    });

    return this.pendingLoginUserPromise;
  }

  async navigateAfterLogin(responseData: any): Promise<boolean> {
    if (responseData?.sectionWithRole && responseData.sectionWithRole.length > 0) {
      // Find a section with active contract (OPEN status)
      const sectionDocuments = responseData.sectionDocuments ?? [];
      const activeSectionWithRole = responseData.sectionWithRole.find((swr: { sectionId: string }) => {
        const sectionDoc = sectionDocuments.find((doc: { id: string }) => doc.id === swr.sectionId);
        return sectionDoc?.contractInfo?.contractStatus === 'OPEN';
      });

      if (activeSectionWithRole) {
        // Navigate to a section with active contract
        return this.router.navigate(['home', activeSectionWithRole.sectionId, 'users']);
      }

      // All sections have expired contracts
      // First, select the section to ensure contractEnabled is set to false
      const firstSection = responseData.sectionWithRole[0];
      await this.setSelectedSection({
        sectionId: firstSection.sectionId,
        sectionName: firstSection.sectionName ?? '',
      });

      // Navigate to first section's unauthorized page
      return this.router.navigate(['home', firstSection.sectionId, 'unauthorized'], {
        queryParams: { reason: 'contract' }
      });
    }

    const systemAdmin = responseData?.systemAdminAssignmentDocument;
    if (systemAdmin != null) {
      return this.router.navigate(['system-admin', 'section-groups']);
    }

    const distributorAgent = responseData?.distributorAgentAssignmentDocuments;
    if (distributorAgent != null && distributorAgent.length > 0) {
      return this.router.navigate(['system-admin', 'distributors']);
    }

    const sectionGroupAdmins = responseData?.sectionGroupAdminAssignmentDocuments;
    if (sectionGroupAdmins && sectionGroupAdmins.length > 0) {
      const sectionGroupId = sectionGroupAdmins[0].sectionGroupId;

      if (this.hasSectionManagement(sectionGroupId)) {
        return this.router.navigate(['section-group-admin', sectionGroupId, 'home']);
      }

      if (this.hasSectionGroupAdminManagement(sectionGroupId)) {
        return this.router.navigate(['section-group-admin', sectionGroupId, 'admins']);
      }
    }

  

    return Promise.resolve(false);
  }

  private setStorageCache(responseData: any) {

    this.cachedLoginUserResponse = responseData;

    this.storeAuth.setLoginUserName(responseData.lastName + ' ' + responseData.firstName);

    if (responseData.sectionWithRole) { // When logged-in user belongs to any section
      // Save data to sessionStorage
      this.setSectionDocuments(responseData.sectionDocuments);

      this.setSectionGroupDocuments(responseData.sectionGroupDocuments);

      this.setSectionWithRole(responseData.sectionWithRole);

      this.setSectionGroupAdmins(responseData.sectionGroupAdminAssignmentDocuments);
      this.setSystemAdmin(responseData.systemAdminAssignmentDocument);
      this.setDistributorAgents(responseData.distributorAgentAssignmentDocuments);

      this.setSectionList(responseData.sectionWithRole);

    }
  }

  // set/get selectedSection ---------------------
  async setSelectedSectionById(sectionId: string): Promise<void> {

    const currentSectionId = this.getSelectedSection()?.sectionId;
    if (currentSectionId === sectionId) {
      return;
    }

    const selectedSection = this.getSectionList().find(s => s.sectionId == sectionId);

    if (selectedSection == null){
      this.resetSelectedSection();
      this.router.navigate(['/not-found']);
      return;
    }
    await this.setSelectedSection({ sectionId: selectedSection.sectionId, sectionName: selectedSection.sectionName });
  }

  async setSelectedSection(selectedSection: Section)  : Promise<void>{

    while (this.getSectionDocuments() === null) {
      // ログインしているユーザが指定されるまで非同期で待つ
      await this.delay(100); // 100ミリ秒待機
    }

    return new Promise((resolve, reject) => {
      // Save the selected section
      this.setSessionData('selectedSection', selectedSection);

      if (selectedSection == null || selectedSection.sectionId == ''){
        this.setSelectedSectionGroup(null);
        this.setSelectedSectionWithRole(null);
        this.setSelectedSectionContractInfo(null);

        this.parametersUpdated.next({ type: 'selectedSectionChanged', sectionId: null });
        resolve();
        return;
      }

      const sectionDocuments = this.getSectionDocuments();
      const _selectedSection = sectionDocuments.find(
        item => item.id === selectedSection.sectionId
      );

      // Find and save the associated section group
      const sectionGroupDocuments = this.getSectionGroupDocuments();
      const selectedSectionGroup = sectionGroupDocuments.find(
        group => group.id === _selectedSection.sectionGroupId
      );
      this.setSelectedSectionGroup(selectedSectionGroup);

      //ログイン中のユーザの選択されたセクション内での Role と ContractInfo を取得して保存する
      const sectionWithRole = this.getSectionWithRole();
      const selectedSectionWithRole = sectionWithRole.find(
        item => item.sectionId === selectedSection.sectionId
      );

      if (selectedSectionWithRole) {
        this.setSelectedSectionWithRole(selectedSectionWithRole);
      } else {
        this.setSelectedSectionWithRole(null);
      }
      const contract = new ContractInfo(_selectedSection.contractInfo.contractStatus, _selectedSection.contractInfo.contractStartAt, _selectedSection.contractInfo.contractEndAt);
      this.setSelectedSectionContractInfo(contract);

      // Skip API request if contract is CLOSE to avoid 400 error
      if (contract.contractStatus === 'CLOSE') {
        this.setUsersList([]);
        this.parametersUpdated.next({ type: 'selectedSectionChanged', sectionId: selectedSection.sectionId });
        resolve();
        return;
      }

      // Clear usersList immediately to trigger loading state in UI
      // This prevents the old section's usersList from being shown during transition
      this.setUsersList(null);

      // Resolve immediately after setting section info - don't block on user list fetch
      // This allows navigation to proceed while user data loads in background
      this.parametersUpdated.next({ type: 'selectedSectionChanged', sectionId: selectedSection.sectionId });
      resolve();

      // Capture sectionId to prevent race condition on rapid section switching
      const requestedSectionId = this.getSelectedSection().sectionId;

      // Fetch users list in background
      this.listUsersService.listUserWithUserData(requestedSectionId, false, true, true).subscribe(
        res => {
          // Only apply result if we're still on the same section (prevents race condition)
          if (this.getSelectedSection()?.sectionId === requestedSectionId) {
            this.setUsersList(res);
            this.parametersUpdated.next({ type: 'usersListUpdated', sectionId: requestedSectionId });
          }
        },
        error => {
          console.error('[DatabaseCacheService] Failed to fetch users list:', error);
          // Only handle error if we're still on the same section
          if (this.getSelectedSection()?.sectionId === requestedSectionId) {
            // Set empty array on error to clear loading state (prevents skeleton from being stuck)
            this.setUsersList([]);
            this.parametersUpdated.next({ type: 'usersListUpdated', sectionId: requestedSectionId });
          }
        }
      );
    }

    )
  }

  /**
   * ログアウト・アカウント切り替え時に認証関連キャッシュを全クリアする。
   * setSelectedSection(null) を呼ぶと sectionDocuments が null になった後に
   * 別のメソッドが無限待機するため、キーを直接削除する。
   */
  clearAuthCache(): void {
    const sessionKeys = [
      'usersList',
      'selectedUserId',
      'selectedSection',
      'selectedSectionWithRole',
      'selectedSectionGroup',
      'selectedSectionContractInfo',
      'selectedSectionRoles',
      'sectionDocuments',
      'sectionGroupDocuments',
      'sectionWithRole',
      'sectionGroupAdmin',
      'systemAdmin',
      'sectionList',
      'selectedUserWithUserData',
    ];
    sessionKeys.forEach(key => sessionStorage.removeItem(key));

    // gokuri.* プレフィックスの認証フローキーもクリア
    const authFlowKeys = [
      'gokuri.loginUiState',
      'gokuri.postLoginRedirectUrl',
      'gokuri.pendingNavigation',
      'gokuri.pendingNavigationMode',
      'gokuri.forcePromptLogin',
      'gokuri.user',
    ];
    authFlowKeys.forEach(key => sessionStorage.removeItem(key));

    // localStorage の認証キーのみクリア（gokuri-theme・gokuri-recent-users は保持）
    localStorage.removeItem('gokuri.loginUserName');

    // メモリキャッシュのリセット
    this.usersDataCache.clear();
    this.cachedLoginUserResponse = null;
    this.pendingLoginUserPromise = null;
    this.pendingLoginUserAndSectionPromise = null;
    this.pendingLoginUserAndSectionKey = null;
    this.pendingMediasFetch.clear();
    this.pendingRecordsFetch.clear();
    this.pendingGostsFetch.clear();
    this.pendingUserDataHistoryFetch.clear();
    this.pendingUserDataFetch.clear();
    this.pendingRecordsDownloadRequestsFetch.clear();
    this.pendingGostRecordFetch.clear();
  }

  resetSelectedSection(): void {
    this.cachedLoginUserResponse = null;
    this.setSelectedSection(null);
  }
  getSelectedSection(): any {
    return this.getSessionData('selectedSection');
  }

  public getJobTitle(jobTitleCode) {
    if (jobTitleCode == null) {
      return DATABASE_CACHE_SERVICE_I18N.jobTitle.unspecified;
    }
    if (jobTitleCode == "") {
      return DATABASE_CACHE_SERVICE_I18N.jobTitle.unspecified;
    }

    var allJobTitles = this.staffJobTitles.concat(this.userJobTitles);
    try {
      return allJobTitles.filter(u => u.code == jobTitleCode)[0].desc;
    }
    catch (e) {
      return DATABASE_CACHE_SERVICE_I18N.jobTitle.unspecified;
    }
  }

  public getDispName(dataItem) {

    const fallbackLanguage = 'jp';

    // そもそも単一の文字列のときはそのまま返す
    if (typeof dataItem  == 'string'){
      return dataItem;
    }

    // angular の localize でビルドされている現在の locale を取得
    // LOCALE_ID が 'ja-JP' のような形式の場合、言語コード 'ja' を抽出する
    const fullLocale = this.locale;
    const language = fullLocale.split('-')[0];

    // dataItem が { "ja": "" } のような形式のときに 言語に対応する表示内容を返す
    // フルロケール（例: 'ja-JP'）と言語コード（例: 'ja'）の両方を確認する
    if (fullLocale in dataItem) {
      return dataItem[fullLocale];
    }
    if (language in dataItem) {
      return dataItem[language];
    }

    // ja のときは jp も確認する。過去に jp で作成してしまったデータがあるので互換性維持のため
    if (language === 'ja') {
      if (fallbackLanguage in dataItem) {
        return dataItem[fallbackLanguage];
      }
    }

    // dataItem が { "displayNames" : {"ja": "" } }のような形式のときに 言語に対応する表示内容を返す
    if (!("displayNames" in dataItem)) {
      return dataItem.name;
    }

    const displayNames = dataItem.displayNames;
    if (fullLocale in displayNames) {
      return displayNames[fullLocale];
    }
    if (language in displayNames) {
      return displayNames[language];
    }

    // ja のときは jp も確認する。過去に jp で作成してしまったデータがあるので互換性維持のため
    if (language === 'ja') {
      if (fallbackLanguage in displayNames) {
        return displayNames[fallbackLanguage];
      }
    }

    // dataItem に 対応言語の表示内容も displayNames も無いときは "name" のアイテムを返す
    if ("name" in dataItem) {
      return dataItem.name;
    }

    // どれにも当てはまらないときはdataItemそのものを文字列にして返す
    return JSON.stringify(dataItem);

  }

  doesListLabelMatch(dataItem, selectedItem): boolean{
    if (typeof dataItem  == 'string'){
      return dataItem == selectedItem;
    }

    for (const key in dataItem) {
      if (dataItem[key] == selectedItem){
        return true;
      }
    }

    return false;
  }

  getListItemDispValue(listValues, selectedValue): string{
    for (var listValue of listValues) {
      if (this.doesListLabelMatch(listValue, selectedValue)){
        return this.getDispName(listValue);
      }
    }

    return selectedValue;
}

  // set/get sectionDocuments ---------------------
  private setSectionDocuments(sectionDocuments: any): void {
    this.setSessionData('sectionDocuments', sectionDocuments);
  }
  getSectionDocuments(): any[] {
    return this.getSessionData('sectionDocuments');
  }

  // set/get sectionGroupDocuments ---------------------
  private setSectionGroupDocuments(sectionGroupDocuments: any): void {
    this.setSessionData('sectionGroupDocuments', sectionGroupDocuments);
  }
  getSectionGroupDocuments(): any[] {
    return this.getSessionData('sectionGroupDocuments');
  }

  // set/get sectionWithRole ---------------------
  private setSectionWithRole(sectionWithRole: any): void {
    this.setSessionData('sectionWithRole', sectionWithRole);
  }
  getSectionWithRole(): any[] {
    return this.getSessionData('sectionWithRole');
  }

  // set/get sectionGroupAdminAssignment ---------------------
  private setSectionGroupAdmins(sectionGroupAdmins: any): void {
    this.setSessionData('sectionGroupAdmin', sectionGroupAdmins);
  }
  getSectionGroupAdmins(): any {
    return this.getSessionData('sectionGroupAdmin');
  }
  /**
   * Permission checks are delegated to PermissionService (future refactor).
   */
  hasSectionGroupAdminRole(sectionGroupId : string, role : string) : boolean{

    let sectionGroupAdmins = this.getSectionGroupAdmins();
    if (sectionGroupAdmins == null){
      return false;
    }

    let sectionGroupAdmin = sectionGroupAdmins.find(a => a.sectionGroupId == sectionGroupId);
    if (sectionGroupAdmin == null){
      return false;
    }

    return sectionGroupAdmin.roles.some(r => r.toUpperCase() == role.toUpperCase());
  }
  hasSectionManagement(sectionGroupId){
    return this.hasSectionGroupAdminRole(sectionGroupId, "SECTION_MANAGEMENT");
  }
  hasSectionGroupUpdate(sectionGroupId){
    return this.hasSectionGroupAdminRole(sectionGroupId, "SECTION_GROUP_UPDATE");
  }
  hasSectionGroupAdminManagement(sectionGroupId){
    return this.hasSectionGroupAdminRole(sectionGroupId, "SECTION_GROUP_ADMIN_MANAGEMENT");
  }

  private setDistributorAgents(distributorAgents: any): void {
    this.setSessionData('distributorAgents', distributorAgents);
  }
  getDistributorAgents(): any {
    return this.getSessionData('distributorAgents');
  }
  hasDistributorAgentRole(distributorId : string) : boolean{
    let distributorAgents = this.getDistributorAgents();
    if (distributorAgents == null){
      return false;
    }
    return distributorAgents.some(agent => agent.distributorId === distributorId);
  } 
  hasAnyDistributorAgentRole() : boolean{
    let distributorAgents = this.getDistributorAgents();
    return (distributorAgents?.length ?? 0) > 0;
  } 
  
  // set/get systemAdmin ---------------------
  private setSystemAdmin(systemAdmin: any): void {
    this.setSessionData('systemAdmin', systemAdmin);
  }
  getSystemAdmin(): any {
    return this.getSessionData('systemAdmin');
  }
  /**
   * Permission checks are delegated to PermissionService (future refactor).
   */
  hasSystemAdminRole(role : string) : boolean{

    let systemAdmin = this.getSystemAdmin();
    if (systemAdmin == null){
      return false;
    }

    return systemAdmin.roles.some(r => r.toUpperCase() == role.toUpperCase());
  }
  hasAllSectionGroupManagement(){
    return  this.hasSystemAdminRole( "ALL_SECTION_GROUP_MANAGEMENT");
  }
  hasParameterUpdate(){
    return this.hasSystemAdminRole( "PARAMETER_MANAGEMENT");
  }
  hasSectionGroupCreate(){
    return this.hasSystemAdminRole( "SECTION_GROUP_CREATE");
  }
  hasSystemAdminManagement(){
    return  this.hasSystemAdminRole( "SYSTEM_ADMIN_MANAGEMENT");
  }
  hasQuotaManagement(){
    return  this.hasSystemAdminRole( "QUOTA_MANAGEMENT");
  }
  hasDistributorManagement(){
    return  this.hasSystemAdminRole( "DISTRIBUTOR_MANAGEMENT");
  }
  hasDistributorAgentManagement(){
    return  this.hasSystemAdminRole( "DISTRIBUTOR_AGENT_MANAGEMENT");
  }
  doesSystemAdminKeyExist(){
    return this.doesKeyExist("systemAdmin");
  }

  // sessionStorageへの保存
  private setSessionData(key: string, data: any): void {
    if (data == undefined){
      data = null;
    }
    sessionStorage.setItem(key, JSON.stringify(data));
  }
  // sessionStorageからの呼び出し
  private getSessionData(key: string): any {
    const data = sessionStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  }
  private doesKeyExist(key: string): boolean{
    const data = sessionStorage.getItem(key);
    return data != null ? true : false;
  }

  // インメモリキャッシュへの保存（キーバリュー）
  private setOnlineCacheData(key: string, data: any): void {
    this.usersDataCache.set(key, data);
  }
  // インメモリキャッシュからの取得
  getOnlineCacheData(key: string): any {
    const data = this.usersDataCache.get(key);
    return data !== undefined ? data : null;
  }

  // インメモリキャッシュへの保存（ユーザーID付きキー）
  private setOnlineCacheDataWithUserId(userId: string, key: string, data: any): void {
    const combinedKey = userId + " : " + key;
    this.usersDataCache.set(combinedKey, data);
  }
  // インメモリキャッシュからの取得（現在選択中ユーザーのデータ）
  getOnlineCacheDataForSelectedUser(key: string): any {
    const combinedKey = this.getSelectedUserId() + " : " + key;
    const data = this.usersDataCache.get(combinedKey);
    return data !== undefined ? data : null;
  }

  // set/get selectedSectionWithRole ---------------------
  private setSelectedSectionWithRole(sectionWithRole: any): void {
    this.setSessionData('selectedSectionWithRole', sectionWithRole);
  }
  getSelectedSectionWithRole(): any {
    return this.getSessionData('selectedSectionWithRole');
  }

  // set/get selectedSectionGroup ---------------------
  private setSelectedSectionGroup(sectionGroup: any): void {
    this.setSessionData('selectedSectionGroup', sectionGroup);
  }
  public forceSetSelectedSectionGroup(sectionGroup: any): void {
    this.setSessionData('selectedSectionGroup', sectionGroup);
  }
  public forceSetSelectedSectionGroupById(sectionGroupId): void {
    const selectedSectionGroup = this.getSectionGroupDocuments().find(
      group => group.id === sectionGroupId
    );
    this.setSelectedSectionGroup(selectedSectionGroup);
    this.parametersUpdated.next({ type: 'selectedSectionGroupChanged', sectionGroupId });
  }
  getSelectedSectionGroup(): any {
    return this.getSessionData('selectedSectionGroup');
  }

  // SelectedSectionGroupAssets は selectedSectionGroup から取り出せるので、Setterは無し
  getSelectedSectionGroupAssets() {
    const selectedSectionGroup = this.getSelectedSectionGroup();
    if (selectedSectionGroup) {
      return selectedSectionGroup.settings.assets;
    }
    return null;
  }

  // logoUrl は selectedSectionGroup から取り出せるので、Setterは無し
  getselectedSectionGroupLogoUrl(){
    let groupAssets = this.getSelectedSectionGroupAssets();
    let logoUrl = null;
    if (groupAssets != null) {
      logoUrl = this.getSelectedSectionGroupAssets().logoUrl;
    }

    if (logoUrl == undefined || logoUrl == "" || logoUrl === "http://") {
      logoUrl = 'https://gokuridevitemstest.blob.core.windows.net/section-group-assets/gokuri_logo_bk.svg?sp=r&st=2023-02-13T07:43:06Z&se=3023-02-13T15:43:06Z&spr=https&sv=2021-06-08&sr=b&sig=QBi1qOR7NcuMrHhEg87%2BxmObo%2F5OD64jy3gtz027hq8%3D';
    }

    return logoUrl;
  }

  // functionAttributes は selectedSectionGroup から取り出せるので、Setterは無し
  getSelectedSectionGroupFunctions() {
    const selectedSectionGroup = this.getSelectedSectionGroup();
    if (selectedSectionGroup) {
      return selectedSectionGroup.settings.functionAttributes;
    }
    return null;
  }

  // userDataAttributes は selectedSectionGroup から取り出せるので、Setterは無し
  getSelectedSectionGroupUserDataAttributes() {
    const selectedSectionGroup = this.getSelectedSectionGroup();
    if (selectedSectionGroup) {
      return selectedSectionGroup.settings.userDataAttributes;
    }

    return null;
  }

  private setSectionList(sectionList: any[]): void {
    this.setSessionData('sectionList', sectionList);
  }

  getSectionList(): { sectionId: string; sectionName: string }[] | null {
    return  this.getSessionData('sectionList');
  }

  private setSelectedSectionContractInfo(contractInfo: ContractInfo): void {
    this.setSessionData('selectedSectionContractInfo', contractInfo);
  }

  getSelectedSectionContractInfo(): ContractInfo {
    return this.getSessionData('selectedSectionContractInfo');
  }

  getSelectedSectionContractEnabled() : boolean {

    const sectionContractInfo = this.getSelectedSectionContractInfo();

    if (sectionContractInfo != null) {
      if (sectionContractInfo.contractStatus === 'CLOSE') {
        return false;
      } else {
        return true;
      }
    }

    return null;

  }

  setSelectedSectionRoles(sectionRoles: string[]): void {
    const selectedSectionWithRole = this.getSelectedSectionWithRole();
    selectedSectionWithRole.permissions.role = sectionRoles;
    this.setSessionData('selectedSectionRoles', selectedSectionWithRole);
  }

  getSelectedSectionRoles(): string[] {
    const selectedSectionWithRole = this.getSelectedSectionWithRole();
    if (selectedSectionWithRole == null){
      return null;
    }
    else{
      return selectedSectionWithRole.permissions.role;
    }
  }

  getSelectedSectionJobTitleCode(){
    const attributes = this.getSelectedSectionWithRole().attributes;
    if (attributes != null) {
      const jobTitleCode = attributes.jobTitleCode;
      if (jobTitleCode != null){
        return jobTitleCode;
      }
    }

    return 'OTHER';
  }

  public displayCurrentTime(step : string){
    const now = new Date();
    const error = new Error();
    const stack = error.stack;
    if (stack) {
      const callerName = stack.split('\n')[2].trim().split(' ')[1];
    }

  }

  getUserType(url){

    if (url[url.length - 1] === '/') {
      url = url.slice(0, -1); // URLの最後の文字がスラッシュの場合は削除する
    }

    const currentSection = url.substring(url.lastIndexOf('/')+1);

    const staffType = currentSection.substring(0,currentSection.indexOf('-'));

    let userType;
    let userTypeDisp;

    switch(staffType){
      case 'staff':
        userType = "STAFF";
        userTypeDisp = DATABASE_CACHE_SERVICE_I18N.userType.staff;
        break;
      case 'supervisor':
        userType = "SUPERVISOR";
        userTypeDisp = DATABASE_CACHE_SERVICE_I18N.userType.supervisor;
        break;
      case 'contributor':
        userType = "CONTRIBUTOR";
        userTypeDisp = DATABASE_CACHE_SERVICE_I18N.userType.contributor;
        break;
    }

    return {userType, userTypeDisp};
  }

  // 指定した時間だけ待機するメソッド
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private createLoginUserAndSelectedSectionRequest(sectionId?: string): Promise<any> {
    return this.setLoginUser()
      .then(async (responseData) => {
        await this.ensureSectionSelectionForResponse(responseData, sectionId);
        return responseData;
      })
      .catch((error) => {
        throw error;
      });
  }

  private canReuseLoginUserData(sectionId?: string): boolean {
    if (!this.cachedLoginUserResponse) {
      return false;
    }

    if (!sectionId) {
      return true;
    }

    return this.cachedLoginUserResponse.sectionWithRole?.some((s: any) => s.sectionId === sectionId);
  }

  private async ensureSectionSelectionForResponse(responseData: any, sectionId?: string): Promise<void> {
    if (!responseData?.sectionWithRole || responseData.sectionWithRole.length === 0) {
      return;
    }

    let targetSectionId = sectionId;

    if (targetSectionId) {
      const hasTarget = responseData.sectionWithRole.some((s: any) => s.sectionId === targetSectionId);
      if (!hasTarget) {
        targetSectionId = responseData.sectionWithRole[0].sectionId;
      }
    } else {
      const currentSectionId = this.getSelectedSection()?.sectionId;
      if (currentSectionId && responseData.sectionWithRole.some((s: any) => s.sectionId === currentSectionId)) {
        return;
      }
      targetSectionId = responseData.sectionWithRole[0].sectionId;
    }

    const currentSectionId = this.getSelectedSection()?.sectionId;
    if (currentSectionId === targetSectionId) {
      return;
    }

    await this.setSelectedSectionById(targetSectionId);
  }
}

export class Section {
  sectionId: string;
  sectionName: string;
}

export class ContractInfo {
  contractStatus: string;
  contractStartAt: string;
  contractEndAt: string;

  constructor(contractStatus: string, contractStartAt: string, contractEndAt: string) {
    this.contractStatus = contractStatus;
    this.contractStartAt = contractStartAt;
    this.contractEndAt = contractEndAt;
  }
}

export interface UserData {
  Name: string;
  Type: string;
  enabled?: boolean;
  required?: boolean;
  Min?: string;
  Max?: string;

}

export interface Breadcrumb {
  label: string;  // 表示するテキスト
  icon?: string;  // オプションのアイコン
  link?: string[];  // リンク先
}
